import axios from 'axios';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../store/general/Functions';
import { parseParams } from '../appFunctionsService';
import { IApiError, IBooleanResponse } from '../../@types/other';
import { IUserListView } from '../../@types/model/user/userListView';
import { IUser } from '../../@types/model/user/user';
import { IMobileUser } from '../../@types/model/user/mobileUser';
import { IUserUpdate } from '../../@types/model/user/userUpdate';
import { IUserAdd } from '../../@types/model/user/UserAdd';
import { IUserToken } from '../../@types/model/user/userToken';
import qs from 'qs';

export async function getList() : Promise<Array<IUserListView>> {
    try {
        const result = await axios.get<Array<IUserListView>>(`${API_URL}/v1/Rights/User/GetList`, {
            params : {
            },
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error getting users');
        }
        return [];
    }
}

export async function getUserScreenListWithAllRights(rightIds ?: Array<number>) : Promise<Array<IUserListView>> {
    try {
        const result = await axios.get<Array<IUserListView>>(`${API_URL}/v1/Rights/User/GetUserScreenList`, {
            params : {
                rightIds,
            },
            paramsSerializer: qs.stringify,
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error getting users');
        }
        return [];
    }
}

export async function get(id : number) : Promise<IUser | undefined> {
    try {
        const result = await axios.get<IUser>(`${API_URL}/v1/Rights/User/Get`, {
            params : {
                id,
            },
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error getting user');
        }
    }
}

export async function addUsers(userData : Array<IUserAdd>) : Promise<Array<IUserListView>> {
    try {
        const result = await axios.post(`${API_URL}/v1/Rights/User/AddUsers`, userData);

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error adding users');
        }
        return [];
    }
}

export async function save(userUpdate : IUserUpdate) : Promise<IUser | null> {
    try {
        const result = await axios.post<IUser>(`${API_URL}/v1/Rights/User/Save`, userUpdate);

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data.Message) === 'string') {
            generalShowErrorSnackbar(error.data.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error saving user');
        }
        return null;
    }
}

export async function requestForgottenPasswordEmail(usernameOrEmail : string) : Promise<boolean> {
    try {
        await axios.post(`${API_URL}/v1/Rights/User/RequestForgottenPasswordEmail`, null, {
            params : { usernameOrEmail },
        });

        return true;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('An error occurred trying to recover your password');
        }
        return false;
    }
}

export async function resetPassword(recoveryCode : string, newPassword : string, email : string) : Promise<IUserToken | null> {
    try {
        const result = await axios.post<IUserToken | null>(`${API_URL}/v1/Rights/User/ResetPassword`, {
            recoveryCode,
            newPassword,
            email,
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('An error occurred trying to update your password');
        }
        return null;
    }
}

export async function resetUserPassword(userId : number) : Promise<boolean> {
    try {
        const result = await axios.post<IUserToken | null>(`${API_URL}/v1/Rights/User/ResetUserPassword`, {
            userId,
        });

        if (result.status === 200) {
            generalShowSuccessSnackbar('User password updated successfully');
            return true;
        }

        generalShowErrorSnackbar('An error occurred trying to update the user\'s password');
        return false;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('An error occurred trying to update the user\'s password');
        }
        return false;
    }
}

export async function setUserPassword(userId : number, password : string) : Promise<boolean> {
    try {
        const result = await axios.post(`${API_URL}/v1/Rights/User/SetUserPassword`, {
            userId,
            password,
        });

        if (result.status === 200) {
            generalShowSuccessSnackbar('User password updated successfully');
            return true;
        }

        generalShowErrorSnackbar('An error occurred trying to update the user\'s password');
        return false;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('An error occurred trying to update the user\'s password');
        }
        return false;
    }
}

export async function deleteUser(userId : number) : Promise<boolean> {
    try {
        await axios.delete(`${API_URL}/v1/Rights/User/Delete`, {
            params : { userId },
        });

        return true;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error deleting user');
        }
        return false;
    }
}

export async function validateEmployeeNumber(employeeNumber : string) : Promise<boolean> {
    try {
        const result = await axios.get<IBooleanResponse>(`${API_URL}/v1/Rights/User/ValidateEmployeeNumber`, {
            params : {
                employeeNumber,
            },
        });

        return result.data.result;

    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error setting employee nr.');
        }
        return false;
    }
}

export async function getMobileUserList() : Promise<Array<IMobileUser>> {
    try {
        const result = await axios.get<Array<IMobileUser>>(`${API_URL}/v1/Rights/User/GetMobileUsers`, {
            params : {
            },
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400) {
            generalShowErrorSnackbar(error.data?.Message);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error getting users');
        }
        return [];
    }
}

export async function saveMobileUser(mobileUserId : string, name : string, isActive : boolean) : Promise<IMobileUser | null> {
    try {
        const result = await axios.patch<IMobileUser>(`${API_URL}/v1/Rights/User/UpdateMobileUser`, {}, {
            params : {
                mobileUserId,
                name,
                isActive,
                // isBakkieUser,
            },
            paramsSerializer: params => parseParams(params),
        });

        return result.data;
    } catch (ex) {
        const error = ex as IApiError;
        if (!!error && error.status === 400 && typeof(error.data) === 'string') {
            generalShowErrorSnackbar(error.data);
        } else if (!!error && error.status !== 401 && error.status !== 403) {
            generalShowErrorSnackbar('Error saving user');
        }
        return null;
    }
}
